import { format } from "date-fns";
import { inject } from "mobx-react";
import { SyntheticEvent, useCallback } from "react";
import * as React from "react";
import type { Store } from "../../../../../../client/stores";
import { VolumeInCircleIcon } from "../../../../../core/components/icons/VolumeInCircleIcon.component";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import { isPWSSessionBackend } from "../../../../../core/lib/auth/backends/pws";
import { coastMediaService } from "../../../../../core/services/PWS";
import { Button, ButtonKind } from "../../../../../core/ui";

interface CoastShowAudioBoxPlayerLinkProps {
    showDate: Date | number;
    store?: Store;
    children: React.ReactNode;
}

interface CoastShowAudioBoxPlayerLinkLoaderProps {
    showDate: Date | number;
    showLoading?: boolean;
    store?: Store;
    children?: React.ReactNode;
}

export const CoastShowAudioBoxPlayerLink = inject("store")(
    ({ showDate, store, children }: CoastShowAudioBoxPlayerLinkProps) => {
        const handleClick = useCallback(
            (e: SyntheticEvent) => {
                e.preventDefault();

                const session = store?.session.currentSession;

                if (!isPWSSessionBackend(session)) {
                    return null;
                }

                const link = `/video-playlist/${format(showDate, "yyyy/MM/d")}/`;
                const params = "width=769,height=800";
                session.executeOnValidSubscription(() => window.open(link, "", params));
            },
            [showDate, store?.session.currentSession],
        );

        if (!store) {
            return null;
        }

        return (
            <section className="audiobox-section coast-player-link">
                <div className="audiobox-section-value">
                    <div className="link-with-icon">
                        <Button kind={ButtonKind.CUSTOMIZED} className="media-link volume" click={handleClick}>
                            <VolumeInCircleIcon fill="#fff" />
                        </Button>
                        <Button kind={ButtonKind.CUSTOMIZED} className="media-link listen" click={handleClick}>
                            Listen with Coast Player
                        </Button>
                    </div>
                    <div className="link-with-icon">{children}</div>
                </div>
            </section>
        );
    },
);

export const CoastShowAudioBoxPlayerLinkLoader = inject("store")(
    ({ showDate, store, children, showLoading = true }: CoastShowAudioBoxPlayerLinkLoaderProps) => {
        const session = store?.session.currentSession;

        if (!store || !isPWSSessionBackend(session)) {
            return null;
        }

        return (
            <Remote
                loader={() => coastMediaService.getMediaEpisodesPlaylist("insider-audio", showDate)}
                cacheKey={`coast-episodes-playlist-${showDate}-${session.state.hasActiveSubscription}`}
                showLoading={showLoading}
            >
                {/* the episodes themselves don't matter, just the count
                so no need to re-render on login/logout */}
                {({ data }) =>
                    data?.videos?.length ? (
                        <CoastShowAudioBoxPlayerLink showDate={showDate}>{children}</CoastShowAudioBoxPlayerLink>
                    ) : (
                        <>{children}</>
                    )
                }
            </Remote>
        );
    },
);
